<template>
  <SoWidget no-padding>
    <div class="w-100 overflow-x-auto">
      <div class="d-flex justify-space-between">
        <CurrentPaymentsWidget />
        <SoDivider vertical class="mx-1 h-auto" />
        <SuggestedPaymentsWidget
          :use-usage-amended-meterpoints="
            reviewPayment.useUsageEstimatedMeterpoints
          "
          is-direct-debit-review
        />
      </div>
    </div>
  </SoWidget>
  <SoWidget class="mt-8">
    <template #title>
      <h5 class="ma-2">New Schedule</h5>
    </template>
    <div class="pa-2">
      <SoForm>
        <div class="d-flex gap-4">
          <SoTextField
            v-model="reviewPayment.amount"
            :placeholder="reviewPayment.suggestedAmount?.toString()"
            :rules="[rules.required]"
            :error="
              reviewPayment.amountIsLowerThanSuggested
                ? `Amount is lower than suggested amount £${reviewPayment.suggestedAmount}`
                : ''
            "
            label="Amount"
            type="number"
            prefix="£"
            class="w-33"
          />
          <SoSelect
            v-model="reviewPayment.dayOfMonth"
            :items="dayOfMonthOptions"
            :rules="[rules.required]"
            label="Day of the Month"
            placeholder="Select"
            class="w-33"
          />
          <SoDatePicker
            v-model="reviewPayment.fromDate"
            :min-date="reviewPayment.fromDate ?? new Date()"
            label="From Date"
            placeholder="Select"
            class="w-33"
            :disabled="!current.hasActivePaymentSchedule.data"
          />
        </div>
      </SoForm>
      <SoCheckbox
        v-model="reviewPayment.isSeasonal"
        label="Seasonal"
        class="my-4"
      />
      <section
        v-if="
          reviewPayment.amount &&
          reviewPayment.dayOfMonth &&
          reviewPayment.fromDate
        "
        class="pb-2"
      >
        <b v-if="displayAmount">{{ displayAmount }}/month</b>
        <p>
          First payment will be taken on
          {{ formatDate(reviewPayment.dateOfFirstPayment!) }}.
        </p>
      </section>
      <template v-if="reviewPayment.amountIsLowerThanSuggested">
        <SoTextarea
          v-model="reviewPayment.note"
          label="Please leave an explanation as to why the customer will be paying less than the suggested amount."
          placeholder="Add note"
          class="my-4"
        />
        <SoAlert v-if="!isEligibleForPaymentPlan" type="error" class="my-2">
          <b
            >You're changing the payment to less than the suggested amount of
            £{{ reviewPayment.suggestedAmount }}.</b
          >
          This is going to put the customer into debt. Please make them aware of
          this and signpost to financial support.
        </SoAlert>
        <SoAlert v-else type="error" class="my-2">
          <b
            >You’re changing the payment amount to less than the suggested
            amount which will increase the customer’s debt.</b
          >
          This customer is in arrears, please check if a
          <router-link
            :to="{ name: RouteNames.CustomerFinancialsSetUpPaymentPlan }"
            >payment plan</router-link
          >
          would be more appropriate.
        </SoAlert>
        <PaymentsOverviewWidget />
      </template>
    </div>
  </SoWidget>
</template>

<script lang="ts">
  import SoAlert from "@/components/SoAlert.vue"
  import SoDivider from "@/components/SoDivider.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoTextarea from "@/components/formControls/SoTextarea.vue"
  import { formatDate, getMonthDays } from "@/helpers/dateHelper"
  import { formatMoney, formatSeasonalAmount } from "@/helpers/moneyHelpers"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useReviewPaymentStore } from "@/stores/reviewPayment"
  import rules from "@/validators/rules"
  import { defineComponent } from "vue"
  import CurrentPaymentsWidget from "../../widgets/CurrentPaymentsWidget.vue"
  import PaymentsOverviewWidget from "../../widgets/PaymentsOverviewWidget.vue"
  import SuggestedPaymentsWidget from "../../widgets/SuggestedPaymentsWidget.vue"
  import dayjs from "dayjs"

  import { RouteNames } from "@/types/router"

  export default defineComponent({
    components: {
      CurrentPaymentsWidget,
      SuggestedPaymentsWidget,
      PaymentsOverviewWidget,
      SoDatePicker,
      SoTextField,
      SoTextarea,
      SoCheckbox,
      SoDivider,
      SoWidget,
      SoSelect,
      SoAlert,
      SoForm,
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        reviewPayment: useReviewPaymentStore(),
        formatDate,
        rules,
        RouteNames,
      }
    },
    computed: {
      dayOfMonthOptions() {
        return getMonthDays().map(String)
      },
      displayAmount() {
        if (this.reviewPayment.amount) {
          return this.reviewPayment.isSeasonal
            ? formatSeasonalAmount({
                amount: this.reviewPayment.amount,
                divider: "or",
              })
            : formatMoney({ amount: this.reviewPayment.amount })
        }

        return null
      },
      isEligibleForPaymentPlan() {
        return this.current.debtPaymentPlanEligibility.data.isEligible
      },
      nextSchedule() {
        return useCurrentAccountStore().nextPaymentSchedule
      },
    },
    created() {
      if (this.nextSchedule.data) {
        this.reviewPayment.dayOfMonth = this.nextSchedule.data.nextPaymentDt
          .date()
          .toString()
      }

      this.reviewPayment.fromDate = !this.current.hasActivePaymentSchedule.data
        ? new Date()
        : dayjs().add(5, "d").toDate()
    },
  })
</script>
